/** Chuẩn hóa alias menu (vanhoa_*, b1.*) để so khớp license/role Văn hóa */
export const normalizeVanHoaMenuModuleKey = (value: string) =>
    String(value || '')
        .trim()
        .replace(/^vanhoa_/, '')
        .replace(/^b\d+\./, '');

/** Nhóm alias: license key (vhFunction) ↔ controller route cũ ↔ modulename menu */
const VH_MODULE_ALIAS_GROUPS: readonly (readonly string[])[] = [
    ['dashboard', 'vanhoa_dashboard'],
    ['daotaovanhoa', 'vanhoa_dotontap'],
    [
        'khaosatvanhoa.hientaituonglai',
        'vanhoa_dotdanhgia_vanhoa',
        'vanhoa_khaosatvanhoa',
        'vanhoa_dotkhaosat',
        'b1.khaosatvanhoa',
    ],
    [
        'khaosatvanhoa.baocaososanh',
        'vanhoa_dotdanhgia_dashboard',
        'vanhoa_baocaoso',
        'b1.bao-cao-so-sanh-van-hoa',
    ],
    ['nentang.danhgia', 'vanhoa_danhgia', 'b2.loithecanhtranh'],
    ['nentang.ngoinha', 'vanhoa_ngoinha', 'vanhoa_ngoinhanvanhoa', 'b3.xaydungnentang'],
    ['nentang.hanhvi', 'vanhoa_hanhvithenchot', 'b2.hanhvithenchot'],
    ['nentang.anle', 'vanhoa_anle', 'b2.anle'],
    [
        'bangkehoach.xuongca',
        'vanhoa_kehoachhanhdong',
        'vanhoa_bangkehoach',
        'vanhoa_xuongcakenhoach',
        'b3.xuongcakenhoach',
    ],
    ['bangkehoach.bangkehoach', 'vanhoa_kehoachhanhdong', 'vanhoa_bangkehoach', 'b3.bangkehoach'],
    ['bangkehoach.actionplan', 'vanhoa_kehoachhanhdong', 'vanhoa_actionplan', 'b3.actionplan'],
    ['bangkehoach.timeline', 'vanhoa_timeline_dashboard', 'vanhoa_timeline', 'b3.timeline'],
    ['danhgia.truongthanhdoanhnghiep', 'vanhoa_danhgiadoanhnghiep', 'b4.danhgiadoanhnghiep'],
    [
        'danhgia.trainghiemnhanvien.tieuchi',
        'vanhoa_tieuchigankettrainghiemdiemcham',
        'vanhoa_gankettrainghiemdiemchamnhanvien',
    ],
    ['danhgia.trainghiemnhanvien.khaosat', 'vanhoa_dotkhaosatganket', 'vanhoa_dotkhaosatganket'],
];

const VH_ALIAS_LOOKUP = (() => {
    const map = new Map<string, Set<string>>();
    const add = (a: string, b: string) => {
        if (!map.has(a)) map.set(a, new Set());
        map.get(a)!.add(b);
    };
    for (const group of VH_MODULE_ALIAS_GROUPS) {
        for (const key of group) {
            for (const alias of group) {
                if (alias !== key) add(key, alias);
            }
        }
    }
    return map;
})();

/** Mở rộng danh sách modulename với alias legacy (route controller, b1/b2/b3, license). */
export const expandVanHoaMenuModules = (modules: string[]): string[] => {
    const expanded = new Set<string>();
    const queue = [...modules];

    while (queue.length > 0) {
        const current = queue.pop()!;
        if (!current || expanded.has(current)) continue;
        expanded.add(current);
        expanded.add(normalizeVanHoaMenuModuleKey(current));

        for (const alias of VH_ALIAS_LOOKUP.get(current) ?? []) {
            if (!expanded.has(alias)) queue.push(alias);
        }

        const normalized = normalizeVanHoaMenuModuleKey(current);
        for (const alias of VH_ALIAS_LOOKUP.get(normalized) ?? []) {
            if (!expanded.has(alias)) queue.push(alias);
        }

        for (const group of VH_MODULE_ALIAS_GROUPS) {
            if (group.some((g) => g === current || normalizeVanHoaMenuModuleKey(g) === normalized)) {
                for (const g of group) {
                    if (!expanded.has(g)) queue.push(g);
                }
            }
        }
    }

    return [...expanded];
};

/** So khớp quyền menu BSC / Quy trình — chỉ exact (giữ hành vi code cũ). */
export const roleMatchesMenuModulesExact = (role: any, arrayModule: string[]): boolean => {
    if (!role?.read) return false;
    const controller = String(role.controller || '').trim();
    if (!controller) return false;

    const action = role.action != null ? String(role.action).trim() : '';
    if (action && action !== 'index') {
        const controllerAction = `${controller}_${action}`;
        const controllerDot = `${controller}.${action}`;
        if (
            arrayModule.includes(controller)
            || arrayModule.includes(controllerAction)
            || arrayModule.includes(controllerDot)
        ) {
            return true;
        }
    }

    return arrayModule.includes(controller);
};

/** So khớp quyền menu Văn hóa — exact + alias vanhoa_*, thietlap.*, license, route controller cũ */
export const roleMatchesVanHoaMenuModules = (role: any, arrayModule: string[]): boolean => {
    const expandedModules = expandVanHoaMenuModules(arrayModule);
    if (roleMatchesMenuModulesExact(role, expandedModules)) return true;

    if (!role?.read) return false;
    const controller = String(role.controller || '').trim();
    if (!controller) return false;

    const expandedControllerKeys = expandVanHoaMenuModules([controller]);
    if (expandedControllerKeys.some((key) => expandedModules.includes(key))) return true;

    if (controller.startsWith('vanhoa_')) {
        const suffix = controller.slice('vanhoa_'.length);
        if (suffix && expandedModules.some((m) => m === suffix || m.endsWith(`.${suffix}`))) return true;
    }

    const thietlapMatch = controller.match(/^thietlap[._](.+)$/i);
    if (thietlapMatch?.[1]) {
        const suffix = thietlapMatch[1];
        if (expandedModules.some((m) => m === suffix || m.endsWith(`.${suffix}`))) return true;
    }

    const controllerCore = normalizeVanHoaMenuModuleKey(controller);
    return expandedModules.some((m) => {
        if (m === controller) return true;
        const moduleCore = normalizeVanHoaMenuModuleKey(m);
        return moduleCore === controllerCore || m === controllerCore;
    });
};

export const matchesVanHoaLicense = (arrayModule: string[], moduleByLicense: string[]) => {
    if (moduleByLicense.length === 0) return false;
    const expandedModules = expandVanHoaMenuModules(arrayModule);
    return moduleByLicense.some((lic) => {
        const expandedLicense = expandVanHoaMenuModules([lic]);
        if (expandedLicense.some((key) => expandedModules.includes(key))) return true;

        const licCore = lic.split('.').pop() || lic;
        return expandedModules.some((m) => {
            if (m === lic) return true;
            const moduleCore = normalizeVanHoaMenuModuleKey(m);
            return (
                lic.includes(moduleCore)
                || moduleCore.includes(licCore)
                || lic.endsWith(`.${moduleCore}`)
                || lic.startsWith(`${moduleCore}.`)
            );
        });
    });
};

export const matchesQuyTrinhLicense = (arrayModule: string[], moduleByLicense: string[]) => {
    if (moduleByLicense.length === 0) return false;
    return moduleByLicense.some((lic) => {
        if (arrayModule.includes(lic)) return true;
        return arrayModule.some((m) => m === lic || m.includes(lic) || lic.includes(m));
    });
};

/** Route guard VH: map controller route ↔ alias license/role */
export const hasVanHoaRouteRead = (
    listRole: Array<{ controller?: string; action?: string; read?: boolean }> | undefined | null,
    routeController: string,
): boolean => {
    if (!Array.isArray(listRole) || !routeController) return false;
    const moduleKeys = expandVanHoaMenuModules([routeController]);
    return listRole.some((role) => roleMatchesVanHoaMenuModules(role, moduleKeys));
};

export const buildVanHoaRouteModuleKeys = (routeController: string) =>
    expandVanHoaMenuModules([routeController]);
